import React, { useEffect, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { CorrectionsOverview, getCorrectionsOverview, getLocations } from '../../services/inventoryCorrections.service';
import { getDetailReasons } from '../../services/reason.service';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  CORRECTION_STATUS_GROUPS,
  DocListRow,
  DocumentTable,
  EmptyState,
  ErrorBanner,
  FilterDef,
  FiltersSheet,
  ListState,
  MenuItem,
  PageFrame,
  Pager,
  PageTitle,
  PhoneDocumentList,
  SharePanel,
  StatusStrip,
  ToolbarCard,
  correctionStatusOf,
  dayMonth,
  periodTextOf,
  rangeOf,
  readListState,
  signedMoney,
  useLanguage,
  useOverviewData,
  writeListState,
} from '../../components/purchasing';

const PAGE_SIZE = 20;
const STATUS_KEYS = CORRECTION_STATUS_GROUPS.map((group) => group.key);
// Shown in the strip whether or not there are any; the rest only when there are.
const ALWAYS = ['all', 'waiting', 'approved', 'rejected'];

/** Stock corrections of the site, everyone's; company admins approve them on the correction page. */
const InventoryCorrections = () => {
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [state, setState] = useState<ListState>(() => readListState(STATUS_KEYS, ['reason']));
  const [term, setTerm] = useState(state.search.trim());
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [reasons, setReasons] = useState<{ id: string; name: string }[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const update = (patch: Partial<ListState>) => setState((prev) => ({ ...prev, page: 1, ...patch }));
  const setFilter = (key: string, value: string) => setState((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, [key]: value } }));

  useEffect(() => writeListState(state), [state]);
  useDebounceEffect(() => setTerm(state.search.trim()), [state.search], 300);

  useEffect(() => {
    const load = () => {
      getLocations(userId, companyId, 0, 0, '')
        .then((response: any) => setSites((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.name ?? '') }))))
        .catch(() => undefined);
      getDetailReasons(companyId, '1')
        .then((response: any) => setReasons((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.reasonName ?? '') }))))
        .catch(() => undefined);
    };
    load();
    document.addEventListener('newToken', load);
    return () => document.removeEventListener('newToken', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, failed, reload } = useOverviewData<CorrectionsOverview>(
    () =>
      getCorrectionsOverview({
        ...rangeOf(state),
        status: state.status === 'all' ? undefined : state.status,
        reasonId: state.filters.reason || undefined,
        search: term || undefined,
        locationId: state.site || undefined,
        page: state.page,
        limit: PAGE_SIZE,
      }),
    [state.period, state.from, state.to, state.status, state.site, state.filters.reason, state.page, term],
    language,
  );

  const siteName = sites.find((site) => site.id === state.site)?.name;
  const periodText = periodTextOf(state, t);
  const totals = data?.totals ?? { corrections: 0, value: 0 };
  const correctionsText = (count: number) => (count === 1 ? t('1 correction') : t('{count} corrections', { count }));
  const linesText = (count: number) => (count === 1 ? t('1 line') : t('{count} lines', { count }));
  // The count matches All in the strip; the impact counts approved corrections only.
  const meta = [
    phone ? null : state.site ? siteName : t('All sites'),
    periodText,
    data ? correctionsText(Number(data.statusCounts?.all ?? 0)) : null,
    data ? t('{amount} stock impact', { amount: signedMoney(totals.value) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const counts = data?.statusCounts ?? {};
  const strip = CORRECTION_STATUS_GROUPS.filter((group) => ALWAYS.includes(group.key) || (counts[group.key] ?? 0) > 0 || state.status === group.key).map((group) => ({
    key: group.key,
    label: t(group.label),
    family: group.family,
    count: Number(counts[group.key] ?? 0),
  }));

  const showSite = !state.site;
  const rows: DocListRow[] = (data?.rows ?? []).map((row) => {
    const created = dayMonth(row.createdAt);
    const person = row.createdBy?.name ?? null;
    const site = showSite ? row.location?.name ?? null : null;
    // A correction without a comment is named by its reasons.
    const subtitle = row.comment?.trim() || row.reasons.join(', ') || null;
    return {
      id: row.id,
      number: `#${row.internalNumber}`,
      subtitle,
      cells: {
        created: { main: created, sub: [person, site].filter(Boolean).join(' · ') || null },
        lines: { main: row.lines },
        value: { main: signedMoney(row.value), strong: true },
      },
      status: correctionStatusOf(row.statusId),
      phoneText: [subtitle, site].filter(Boolean).join(' · ') || linesText(row.lines),
      phoneMeta: [created, person, subtitle ? linesText(row.lines) : null].filter(Boolean).join(' · '),
      phoneAmount: signedMoney(row.value),
    };
  });

  const hrefOf = (row: DocListRow) => `/${preLanguage}/corrections/${row.id}`;
  const open = (row: DocListRow) => webNavigate(hrefOf(row));
  const menuFor = (row: DocListRow): MenuItem[] => [
    { key: 'open', label: t('Open correction'), icon: 'arrowRight', onSelect: () => open(row) },
    { key: 'copy', label: t('Copy to new correction'), icon: 'copy', onSelect: () => webNavigate(`/${preLanguage}/corrections/new?copy=${row.id}`) },
  ];

  const filters: FilterDef[] = [
    { key: 'site', label: 'Site', icon: 'pin', allLabel: 'All sites', value: state.site, options: sites, onChange: (value) => update({ site: value }) },
    { key: 'reason', label: 'Reason', icon: 'box', allLabel: 'All reasons', value: state.filters.reason, options: reasons, onChange: (value) => setFilter('reason', value) },
  ];

  const newCorrection = () => webNavigate(`/${preLanguage}/corrections/new`);
  const statusLabel = CORRECTION_STATUS_GROUPS.find((group) => group.key === state.status)?.label;
  const empty = term ? (
    <EmptyState icon="search" phone={phone} title={t('No corrections match "{search}"', { search: term })} desc={t('Check the number, or search by comment or product.')} />
  ) : state.status !== 'all' && statusLabel ? (
    <EmptyState phone={phone} title={t('No corrections with this status')} desc={t('{status} corrections in this period show here.', { status: t(statusLabel) })} />
  ) : (
    <EmptyState
      phone={phone}
      title={t('No corrections in this period')}
      desc={t('Record breakage, waste or a count difference with a correction.')}
      action={
        <Button variant="primary" phone={phone} icon="plus" onClick={newCorrection}>
          {t('New correction')}
        </Button>
      }
    />
  );

  const pager = data ? <Pager phone={phone} t={t} page={state.page} limit={PAGE_SIZE} total={data.count} onPage={(page) => setState((prev) => ({ ...prev, page }))} /> : null;
  const list = phone ? (
    <PhoneDocumentList t={t} label={t('Corrections')} rows={rows} loading={loading} hrefOf={hrefOf} onOpen={open} empty={empty} footer={pager} />
  ) : (
    <DocumentTable
      t={t}
      label={t('Corrections')}
      numberLabel={t('Correction')}
      columns={[
        { key: 'created', label: t('Created') },
        { key: 'lines', label: t('Lines'), align: 'right', compact: true },
        { key: 'value', label: t('Value'), align: 'right', compact: true },
      ]}
      rows={rows}
      loading={loading}
      hrefOf={hrefOf}
      onOpen={open}
      menuFor={menuFor}
      menuLabel={(row) => t('Actions for correction {number}', { number: row.number })}
      triggerLabel={(row) => t('More actions for correction {number}', { number: row.number })}
      empty={empty}
      footer={pager}
    />
  );

  const impact = Math.abs(totals.value);
  const panel = (
    <SharePanel
      label={t('By reason')}
      title={t('By reason')}
      subtitle={data ? t('{period} · {amount} stock impact, approved only', { period: periodText, amount: signedMoney(totals.value) }) : undefined}
      empty={t('No approved corrections in this period.')}
      rows={(data?.byReason ?? []).map((row) => {
        const share = impact > 0 ? Math.round((Math.abs(Number(row.value)) / impact) * 100) : 0;
        return { key: row.reasonId, name: row.name ?? t('No reason'), value: signedMoney(row.value), detail: `${linesText(row.lines)} · ${share}%`, share };
      })}
    />
  );

  return (
    <PageFrame phone={phone}>
      <PageTitle phone={phone} t={t} title={t('Corrections')} meta={meta} primary={{ key: 'new', label: t('New correction'), icon: 'plus', onSelect: newCorrection }} />
      <ToolbarCard
        phone={phone}
        t={t}
        period={state.period}
        onPeriod={(period) => update({ period })}
        customFrom={state.from}
        customTo={state.to}
        onCustom={(from, to) => update({ from, to })}
        search={state.search}
        onSearch={(search) => setState((prev) => ({ ...prev, search, page: 1 }))}
        searchLabel={t('Search corrections')}
        searchPlaceholder={phone ? t('Search corrections') : t('Number, comment or product')}
        filters={filters}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <StatusStrip phone={phone} label={t('Correction status')} items={strip} active={state.status} onPick={(status) => update({ status })} />
      {failed && <ErrorBanner message={t('The corrections could not be loaded.')} retryLabel={t('Try again')} onRetry={reload} />}
      {phone ? (
        list
      ) : (
        <div className="pu-split-wide" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 320px)', gap: '24px', alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>{list}</div>
          <div style={{ minWidth: 0 }}>{panel}</div>
        </div>
      )}
      {phone && (
        <FiltersSheet
          open={filtersOpen}
          t={t}
          filters={filters}
          showLabel={data?.count === 1 ? t('Show 1 correction') : t('Show {count} corrections', { count: data?.count ?? 0 })}
          onClear={() => setState((prev) => ({ ...prev, page: 1, site: '', filters: { ...prev.filters, reason: '' } }))}
          onClose={() => setFiltersOpen(false)}
        />
      )}
    </PageFrame>
  );
};

export default InventoryCorrections;
